<template>
  <VaSidebar v-model="writableVisible" :width="sidebarWidth" :color="color" minimized-width="0">
    <VaAccordion v-model="value" multiple>
      <VaCollapse v-for="(route, index) in navigationRoutes" :key="index">
        <template #header="{ value: isCollapsed }">
          <VaSidebarItem
            :to="route.children.length > 0 ? undefined : { name: route.name }"
            :active="routeHasActiveChild(route)"
            :active-color="activeColor"
            :text-color="textColor(route)"
            :aria-label="`${route.children ? 'Open category ' : 'Visit'} ${route.meta.displayName}`"
            role="button"
            hover-opacity="0.10"
          >
            <VaSidebarItemContent class="py-3 pr-2 pl-4">
              <VaIcon
                v-if="route.meta.icon"
                aria-hidden="true"
                :name="route.meta.icon"
                size="20px"
                :color="iconColor(route)"
              />
              <VaSidebarItemTitle class="flex justify-between items-center leading-5 font-semibold">
                {{ route.meta.displayName }}
                <VaIcon v-if="route.children.length > 0" :name="arrowDirection(isCollapsed)" size="20px" />
              </VaSidebarItemTitle>
            </VaSidebarItemContent>
          </VaSidebarItem>
        </template>
        <template #body>
          <div v-for="(childRoute, index2) in route.children" :key="index2">
            <VaSidebarItem
              :to="{ name: childRoute.name }"
              :active="isActiveChildRoute(childRoute)"
              :active-color="activeColor"
              :text-color="textColor(childRoute)"
              :aria-label="`${route.meta.displayName}로 이동`"
              hover-opacity="0.10"
            >
              <VaSidebarItemContent class="py-3 pr-2 pl-14">
                <VaIcon
                  v-if="childRoute.meta?.icon"
                  aria-hidden="true"
                  :name="childRoute.meta?.icon"
                  size="20px"
                  :color="iconColor(childRoute)"
                />
                <VaSidebarItemTitle class="leading-5 font-semibold">
                  {{ childRoute.meta?.displayName ?? childRoute.name }}
                </VaSidebarItemTitle>
              </VaSidebarItemContent>
            </VaSidebarItem>
          </div>
        </template>
      </VaCollapse>
    </VaAccordion>
  </VaSidebar>
</template>
<script lang="ts">
import { defineComponent, watch, ref, computed } from 'vue';
import { RouteLocationMatched, RouteRecordNormalized, RouteRecordRaw, useRoute, useRouter } from 'vue-router';

import { useI18n } from 'vue-i18n';
import { useColors } from 'vuestic-ui';

export default defineComponent({
  name: 'Sidebar',
  props: {
    visible: { type: Boolean, default: true },
    mobile: { type: Boolean, default: false },
  },
  emits: ['update:visible'],

  setup: (props, { emit }) => {
    const { getColor, colorToRgba } = useColors();
    const route = useRoute();
    const router = useRouter();
    const { t } = useI18n();

    const value = ref<boolean[]>([]);

    const writableVisible = computed({
      get: () => props.visible,
      set: (v: boolean) => emit('update:visible', v),
    });

    const navigationRoutes = computed(() => {
      return router
        .getRoutes()
        .filter((r) => !!r.meta.displayName && (r.meta.depth ?? 0) <= 1)
        .map((r) => {
          if (r.name === 'dashboard') return { ...r, children: [] };
          return r;
        });
    });
    const isActiveChildRoute = (child: RouteRecordNormalized | RouteRecordRaw) => route.name === child.name;

    const routeHasActiveChild = (section: RouteRecordNormalized | RouteRecordRaw) => {
      return route.matched.findIndex((t: RouteLocationMatched) => t.name === section.name) !== -1;
    };

    const setActiveExpand = () =>
      (value.value = navigationRoutes.value.map((route: RouteRecordNormalized) => routeHasActiveChild(route)));

    const sidebarWidth = computed(() => (props.mobile ? '100vw' : '280px'));
    const color = computed(() => getColor('background-secondary'));
    const activeColor = computed(() => colorToRgba(getColor('focus'), 0.1));

    const iconColor = (route: RouteRecordNormalized | RouteRecordRaw) =>
      routeHasActiveChild(route) ? 'primary' : 'secondary';
    const textColor = (route: RouteRecordNormalized | RouteRecordRaw) =>
      routeHasActiveChild(route) ? 'primary' : 'textPrimary';
    const arrowDirection = (state: boolean) => (state ? 'va-arrow-up' : 'va-arrow-down');

    watch(() => route.fullPath, setActiveExpand, { immediate: true });

    return {
      writableVisible,
      sidebarWidth,
      value,
      color,
      activeColor,
      navigationRoutes,
      routeHasActiveChild,
      isActiveChildRoute,
      t,
      iconColor,
      textColor,
      arrowDirection,
    };
  },
});
</script>
